import { useCallback, useEffect, useRef, useState } from "react";

import scraperApi from "api";
import { useDebouncer } from "hooks/useDebouncer";
import { Job } from "providers/HostedScrapingProvider/types";
import { isJobActive } from "components/hosted-scraping/job-list/utils";

export type SubTaskRow = {
  id: number;
  input: string;
  status: "running" | "finished" | "failed" | "cancelled";
  attempts: number | null;
  nextRetry: string | null;
  terminationReason: string | null;
  finishedAt: string | null;
  createdAt: string;
  cost: number | null;
  hasResult: boolean;
};

// undefined = no filter
export type SubtaskStatusFilter = SubTaskRow["status"] | undefined;

type SubTasksResponse = { job: Job | null; subtasks: SubTaskRow[]; totalCount: number };

const PAGE_SIZE = 25;
const POLL_INTERVAL = 5000;
const SEARCH_DEBOUNCE_MS = 300;

export interface UseJobSubtasksResult {
  job: Job | null;
  subtasks: SubTaskRow[];
  totalCount: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  statusFilter: SubtaskStatusFilter;
  setStatusFilter: (status: SubtaskStatusFilter) => void;
  searchInput: string;
  setSearchInput: (search: string) => void;
  hasActiveFilters: boolean;
  isLoading: boolean;
  firstLoad: boolean;
  error: string | null;
  refetch: () => void;
  retry: () => void;
}

export const useJobSubtasks = (
  projectId: number,
  jobId: number,
): UseJobSubtasksResult => {
  const [data, setData] = useState<SubTasksResponse | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatus] = useState<SubtaskStatusFilter>(undefined);
  const [searchInput, setSearch] = useState("");
  const debouncedSearch = useDebouncer(searchInput, SEARCH_DEBOUNCE_MS);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Ignore responses from superseded fetches (page change / refetch / poll).
  const fetchIdRef = useRef(0);
  // Polls skip while a request is in flight, otherwise slow responses keep superseding each other.
  const inFlightRef = useRef(false);

  const fetchPage = useCallback(
    (isBackground = false) => {
      if (isBackground && inFlightRef.current) return;
      const fetchId = ++fetchIdRef.current;
      inFlightRef.current = true;
      // A background poll only clears the error once it succeeds, so a failing poll can't hide it.
      if (!isBackground) {
        setIsLoading(true);
        setError(null);
      }
      const offset = (page - 1) * PAGE_SIZE;
      scraperApi.hostedScraping
        .subtasks(projectId, jobId, PAGE_SIZE, offset, statusFilter, debouncedSearch.trim())
        .then((res: SubTasksResponse) => {
          if (fetchId !== fetchIdRef.current) return;
          setError(null);
          setData(res);
          // Polling can shrink a filtered total below the current offset; step back to the last valid page.
          const lastPage = Math.max(1, Math.ceil(res.totalCount / PAGE_SIZE));
          if (page > lastPage) setPage(lastPage);
        })
        .catch(() => {
          // Don't blank a rendered table on a failed background poll; the next cycle retries.
          if (fetchId === fetchIdRef.current && !isBackground) {
            setError("Couldn't load per-URL details. Please try again.");
          }
        })
        .finally(() => {
          if (fetchId !== fetchIdRef.current) return;
          inFlightRef.current = false;
          setIsLoading(false);
        });
    },
    [projectId, jobId, page, statusFilter, debouncedSearch],
  );

  const setSearchInput = useCallback((next: string) => {
    setSearch(next);
    setPage(1);
  }, []);

  // Select re-fires onChange on mount, so only reset the page on a real change.
  const setStatusFilter = useCallback((next: SubtaskStatusFilter) => {
    if (next === statusFilter) return;
    setStatus(next);
    setPage(1);
  }, [statusFilter]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  const job = data?.job ?? null;
  const active = job !== null && isJobActive(job);

  useEffect(() => {
    if (!active) return;
    const timer = setInterval(() => fetchPage(true), POLL_INTERVAL);
    return () => clearInterval(timer);
  }, [active, fetchPage]);

  // refetch() refreshes silently; retry() shows the loading state and clears the error.
  const refetch = useCallback(() => fetchPage(true), [fetchPage]);
  const retry = useCallback(() => fetchPage(), [fetchPage]);

  return {
    job,
    subtasks: data?.subtasks ?? [],
    totalCount: data?.totalCount ?? 0,
    page,
    pageSize: PAGE_SIZE,
    setPage,
    statusFilter,
    setStatusFilter,
    searchInput,
    setSearchInput,
    hasActiveFilters: statusFilter !== undefined || debouncedSearch.trim() !== "",
    isLoading,
    firstLoad: isLoading && data === undefined,
    error,
    refetch,
    retry,
  };
};
